/**
 * AIExportModal - Export account technology portfolio for NotebookLM/AI analysis
 *
 * Generates structured Markdown exports optimized for LLM consumption.
 * Supports clipboard copy, file download, and signed URL generation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import useModalBehavior from '../../hooks/useModalBehavior';
import { saveOrShare } from '../../platform';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  X, Download, Copy, Check, FileText, AlertCircle,
  ChevronDown, ChevronRight, Loader2, ExternalLink, ClipboardCopy, Database,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './AIExportModal.css';

function fmtBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtMoney(value) {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

const STORAGE_KEY = 'ai-export-history';

function getExportHistory() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function setExportHistory(departmentId, timestamp) {
  const history = getExportHistory();
  history[departmentId] = timestamp;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
}

function AIExportModal({ departmentId, departmentName, acronym, onClose }) {
  const [exportType, setExportType] = useState('raw'); // 'raw' = all line items, 'summary' = executive summary
  const [years, setYears] = useState(5);
  const [includeLineItems, setIncludeLineItems] = useState(false);
  const [includeVendorAnalysis, setIncludeVendorAnalysis] = useState(true);
  const [includeClassifications, setIncludeClassifications] = useState(true);
  const [includeSpendTrends, setIncludeSpendTrends] = useState(true);
  const [includeRenewals, setIncludeRenewals] = useState(true);
  const [includeCompetitive, setIncludeCompetitive] = useState(true);
  const [includeContacts, setIncludeContacts] = useState(true);
  const [includePipeline, setIncludePipeline] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [exportError, setExportError] = useState(null);

  const lastExport = getExportHistory()[departmentId];

  // Fetch size estimate
  const { data: estimate, isLoading: estimating } = useQuery({
    queryKey: ['ai-export-estimate', departmentId, years, includeLineItems],
    queryFn: () => apiClient.get(
      `/api/ai-export/${departmentId}/estimate?years=${years}&includeLineItems=${includeLineItems}`
    ),
    enabled: !!departmentId,
    staleTime: 60000,
  });

  const estimateData = estimate?.estimate || {};

  // Generate export mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (exportType === 'raw') {
        // Raw data export - all line items, no summaries
        const params = new URLSearchParams({ years: String(years) });
        return apiClient.get(`/api/ai-export/${departmentId}/raw?${params}`);
      } else {
        // Summary export - executive summary with analysis
        const params = new URLSearchParams({
          years: String(years),
          includeLineItems: String(includeLineItems),
          includeVendorAnalysis: String(includeVendorAnalysis),
          includeClassifications: String(includeClassifications),
          includeSpendTrends: String(includeSpendTrends),
          includeRenewals: String(includeRenewals),
          includeCompetitive: String(includeCompetitive),
          includeContacts: String(includeContacts),
          includePipeline: String(includePipeline),
        });
        return apiClient.get(`/api/ai-export/${departmentId}/markdown?${params}`);
      }
    },
    onSuccess: (data) => {
      setExportError(null);
      setGeneratedContent(data.content);
      setExportHistory(departmentId, new Date().toISOString());
    },
    onError: (err) => {
      setExportError(err?.message || 'Export generation failed');
    },
  });

  const handleCopyToClipboard = useCallback(async () => {
    setExportError(null);
    if (!generatedContent) {
      setGenerating(true);
      try {
        const result = await generateMutation.mutateAsync();
        await navigator.clipboard.writeText(result.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Generation errors are set by onError; a clipboard rejection
        // (Safari can drop the user gesture after an awaited fetch) still
        // has the content — tell the user to copy from the download path.
        if (generateMutation.isError || !generateMutation.data) {
          setExportError(err?.message || 'Export generation failed');
        } else {
          setExportError('Copy was blocked by the browser — use Download instead.');
        }
      } finally {
        setGenerating(false);
      }
    } else {
      try {
        await navigator.clipboard.writeText(generatedContent);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        setExportError('Copy was blocked by the browser — use Download instead.');
      }
    }
  }, [generatedContent, generateMutation]);

  const handleDownload = useCallback(async () => {
    setExportError(null);
    setGenerating(true);
    try {
      let content = generatedContent;
      if (!content) {
        const result = await generateMutation.mutateAsync();
        content = result.content;
      }

      const suffix = exportType === 'raw' ? 'raw_data' : 'summary';
      const blob = new Blob([content], { type: 'text/markdown' });
      // Web: anchor download. Native iOS: share sheet (no download UX in WKWebView).
      await saveOrShare(blob, `${acronym || 'account'}_${suffix}_${new Date().toISOString().split('T')[0]}.md`);
    } catch (err) {
      setExportError(err?.message || 'Export generation failed');
    } finally {
      setGenerating(false);
    }
  }, [generatedContent, generateMutation, acronym, exportType]);

  // Reset content when options change
  useEffect(() => {
    setGeneratedContent(null);
    setExportError(null);
  }, [exportType, years, includeLineItems, includeVendorAnalysis, includeClassifications, includeSpendTrends, includeRenewals, includeCompetitive, includeContacts, includePipeline]);

  const recommendedMethod = estimateData.recommendation || 'download';
  const isLargeExport = estimateData.estimatedBytes > 200000; // 200KB

  const modal = useModalBehavior(onClose);

  return (
    <div className="aie-overlay" {...modal.overlayProps}>
      <div className="aie-modal" {...modal.dialogProps} aria-label="Export for AI analysis">
        <header className="aie-header">
          <h2>
            <FileText size={20} />
            Export for AI Analysis
          </h2>
          <button className="aie-close" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </header>

        <div className="aie-body">
          {/* Account info */}
          <div className="aie-account-info">
            <span className="aie-account-name">{departmentName}</span>
            {acronym && <span className="aie-account-badge">{acronym}</span>}
          </div>

          {/* Size estimate */}
          {estimating ? (
            <div className="aie-estimate aie-loading">
              <Loader2 size={16} className="aie-spinner" />
              Analyzing account data...
            </div>
          ) : estimateData.totalPOs > 0 ? (
            <div className="aie-estimate">
              <div className="aie-estimate-row">
                <span className="aie-estimate-label">Data range:</span>
                <span className="aie-estimate-value">{years} years</span>
              </div>
              <div className="aie-estimate-row">
                <span className="aie-estimate-label">Purchase orders:</span>
                <span className="aie-estimate-value">{estimateData.totalPOs?.toLocaleString()}</span>
              </div>
              <div className="aie-estimate-row">
                <span className="aie-estimate-label">Line items:</span>
                <span className="aie-estimate-value">{estimateData.totalLineItems?.toLocaleString()}</span>
              </div>
              <div className="aie-estimate-row">
                <span className="aie-estimate-label">Total spend:</span>
                <span className="aie-estimate-value">{fmtMoney(estimateData.totalSpend)}</span>
              </div>
              <div className="aie-estimate-row aie-estimate-size">
                <span className="aie-estimate-label">Estimated export size:</span>
                <span className="aie-estimate-value">{fmtBytes(estimateData.estimatedBytes || 0)}</span>
              </div>
              {lastExport && (
                <div className="aie-estimate-row aie-last-export">
                  <span className="aie-estimate-label">Last exported:</span>
                  <span className="aie-estimate-value">
                    {new Date(lastExport).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="aie-estimate aie-no-data">
              <AlertCircle size={16} />
              No procurement data found for this account.
            </div>
          )}

          {/* Recommendation banner - only show for summary mode */}
          {exportType === 'summary' && estimateData.totalPOs > 0 && (
            <div className={`aie-recommendation aie-rec-${recommendedMethod}`}>
              {recommendedMethod === 'clipboard' && (
                <>
                  <ClipboardCopy size={16} />
                  <span>Recommended: Copy to clipboard and paste into NotebookLM</span>
                </>
              )}
              {recommendedMethod === 'download' && (
                <>
                  <Download size={16} />
                  <span>Recommended: Download file and upload to NotebookLM</span>
                </>
              )}
              {recommendedMethod === 'chunked' && (
                <>
                  <AlertCircle size={16} />
                  <span>Large export: Consider excluding line items or reducing time range</span>
                </>
              )}
            </div>
          )}

          {/* Options */}
          <div className="aie-options">
            {/* Export Type Selector */}
            <div className="aie-option">
              <label htmlFor="aie-export-type">Export type</label>
              <select
                id="aie-export-type"
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
              >
                <option value="raw">Raw Data (all line items)</option>
                <option value="summary">Executive Summary (analysis)</option>
              </select>
            </div>

            {exportType === 'raw' && (
              <div className="aie-recommendation aie-rec-clipboard">
                <Database size={16} />
                <span>Exports every line item with vendor, date, description, amount, and classification</span>
              </div>
            )}

            <div className="aie-option">
              <label htmlFor="aie-years">Time range</label>
              <select
                id="aie-years"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
              >
                <option value={1}>Last 1 year</option>
                <option value={2}>Last 2 years</option>
                <option value={3}>Last 3 years</option>
                <option value={5}>Last 5 years</option>
                <option value={10}>Last 10 years</option>
              </select>
            </div>

            {/* Summary-specific options */}
            {exportType === 'summary' && (
              <>
                <div className="aie-checkbox-option">
                  <label>
                    <input
                      type="checkbox"
                      checked={includeLineItems}
                      onChange={(e) => setIncludeLineItems(e.target.checked)}
                    />
                    Include line item details
                    <span className="aie-option-note">
                      {isLargeExport ? '(significantly increases size)' : '(adds ~50% more content)'}
                    </span>
                  </label>
                </div>

                {/* Advanced options */}
                <button
                  className="aie-advanced-toggle"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Advanced options
                </button>

                {showAdvanced && (
                  <div className="aie-advanced-options">
                    <div className="aie-checkbox-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeVendorAnalysis}
                          onChange={(e) => setIncludeVendorAnalysis(e.target.checked)}
                        />
                        Include vendor relationship analysis
                      </label>
                    </div>
                    <div className="aie-checkbox-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeClassifications}
                          onChange={(e) => setIncludeClassifications(e.target.checked)}
                        />
                        Include technology classifications
                      </label>
                    </div>
                    <div className="aie-checkbox-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeSpendTrends}
                          onChange={(e) => setIncludeSpendTrends(e.target.checked)}
                        />
                        Include spend trends by fiscal year
                      </label>
                    </div>

                    <div className="aie-options-divider">Strategic Context</div>

                    <div className="aie-checkbox-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeRenewals}
                          onChange={(e) => setIncludeRenewals(e.target.checked)}
                        />
                        Include renewals calendar (next 24 months)
                      </label>
                    </div>
                    <div className="aie-checkbox-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeCompetitive}
                          onChange={(e) => setIncludeCompetitive(e.target.checked)}
                        />
                        Include competitive footprint analysis
                      </label>
                    </div>
                    <div className="aie-checkbox-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeContacts}
                          onChange={(e) => setIncludeContacts(e.target.checked)}
                        />
                        Include key contacts / org chart
                      </label>
                    </div>
                    <div className="aie-checkbox-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={includePipeline}
                          onChange={(e) => setIncludePipeline(e.target.checked)}
                        />
                        Include pipeline & opportunities
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Error state — a failed generation must never be silent */}
          {exportError && (
            <div className="aie-estimate aie-no-data" role="alert">
              <span>Export failed: {exportError}</span>
              <button
                className="aie-action aie-action-secondary"
                style={{ marginLeft: 'auto' }}
                onClick={handleDownload}
                disabled={generating}
              >
                Retry
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="aie-actions">
            <button
              className="aie-action aie-action-primary"
              onClick={handleDownload}
              disabled={generating || !estimateData.totalPOs}
            >
              {generating ? (
                <Loader2 size={16} className="aie-spinner" />
              ) : (
                <Download size={16} />
              )}
              Download {exportType === 'raw' ? 'Raw Data' : 'Summary'}
            </button>

            <button
              className="aie-action aie-action-secondary"
              onClick={handleCopyToClipboard}
              disabled={generating || !estimateData.totalPOs}
            >
              {generating ? (
                <Loader2 size={16} className="aie-spinner" />
              ) : copied ? (
                <Check size={16} />
              ) : (
                <Copy size={16} />
              )}
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          </div>

          {/* NotebookLM instructions */}
          <div className="aie-instructions">
            <h4>How to use with NotebookLM</h4>
            <ol>
              <li>Click "Download {exportType === 'raw' ? 'Raw Data' : 'Summary'}"</li>
              <li>Open <a href="https://notebooklm.google.com" target="_blank" rel="noopener noreferrer">NotebookLM <ExternalLink size={12} /></a></li>
              <li>Create a new notebook or open existing</li>
              <li>Click "Add source" &rarr; "Upload file" and select the downloaded .md file</li>
              <li>Ask questions about the account's technology purchases</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIExportModal;
